// src/store/thunks/karigar/jobThunks.js
// ============================================================================
// KARIGAR JOB THUNKS - UPDATED VERSION
// ============================================================================
// Changes:
// 1. Added removeJobDeduction thunk
// 2. Better documentation for payment-related thunks
// 3. Consistent error handling
// ============================================================================

import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../services/api";

// ============================================================================
// API BASE URLS
// ============================================================================
const ADMIN_JOBS_URL = "/admin-karigar/jobs";
const ORDER_KARIGAR_URL = "/order-item-karigar";

// ============================================================================
// HELPER: Create FormData from job data with images
// ============================================================================

const createJobFormData = (jobData, referenceImages = [], itemImages = []) => {
  const formData = new FormData();

  // Append all non-file fields
  Object.keys(jobData).forEach((key) => {
    const value = jobData[key];
    if (value !== undefined && value !== null) {
      if (typeof value === "object" && !(value instanceof File)) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    }
  });

  // Append reference images
  if (referenceImages && referenceImages.length > 0) {
    referenceImages.forEach((file) => {
      formData.append("referenceImages", file);
    });
  }

  // Append item images
  if (itemImages && itemImages.length > 0) {
    itemImages.forEach((file) => {
      formData.append("itemImages", file);
    });
  }

  return formData;
};

// ============================================================================
// JOB LISTING & SEARCH
// ============================================================================

export const fetchJobs = createAsyncThunk(
  "karigar/job/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(ADMIN_JOBS_URL, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const fetchJobById = createAsyncThunk(
  "karigar/job/fetchById",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_JOBS_URL}/${jobId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const fetchJobStats = createAsyncThunk(
  "karigar/job/fetchStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_JOBS_URL}/stats`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const fetchJobTypes = createAsyncThunk(
  "karigar/job/fetchTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_JOBS_URL}/types`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Send admin message to job
 * POST /api/karigar/jobs/:jobId/messages
 */
export const sendJobMessage = createAsyncThunk(
  "job/sendMessage",
  async ({ jobId, message, attachments }, { rejectWithValue }) => {
    try {
      const hasAttachments = attachments && attachments.length > 0;

      if (hasAttachments) {
        const formData = new FormData();
        formData.append("message", message);
        formData.append("sender", "ADMIN");
        attachments.forEach((file) => {
          formData.append("attachments", file);
        });

        const response = await api.post(
          `${ADMIN_JOBS_URL}/${jobId}/messages`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data;
      } else {
        const response = await api.post(`${ADMIN_JOBS_URL}/${jobId}/messages`, {
          message,
          sender: "ADMIN",
        });
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to send message" }
      );
    }
  }
);

// ============================================================================
// JOB CREATION (with image upload support)
// ============================================================================

export const createJob = createAsyncThunk(
  "karigar/job/create",
  async ({ jobData, referenceImages, itemImages }, { rejectWithValue }) => {
    try {
      // Check if we have images to upload
      const hasImages =
        (referenceImages && referenceImages.length > 0) ||
        (itemImages && itemImages.length > 0);

      if (hasImages) {
        const formData = createJobFormData(
          jobData,
          referenceImages,
          itemImages
        );
        const response = await api.post(`${ADMIN_JOBS_URL}/create`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
      } else {
        const response = await api.post(`${ADMIN_JOBS_URL}/create`, jobData);
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const createRepairJob = createAsyncThunk(
  "karigar/job/createRepair",
  async ({ jobData, referenceImages, itemImages }, { rejectWithValue }) => {
    try {
      const hasImages =
        (referenceImages && referenceImages.length > 0) ||
        (itemImages && itemImages.length > 0);

      if (hasImages) {
        const formData = createJobFormData(
          jobData,
          referenceImages,
          itemImages
        );
        const response = await api.post(`${ADMIN_JOBS_URL}/repair`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
      } else {
        const response = await api.post(`${ADMIN_JOBS_URL}/repair`, jobData);
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const createAlterationJob = createAsyncThunk(
  "karigar/job/createAlteration",
  async ({ jobData, referenceImages, itemImages }, { rejectWithValue }) => {
    try {
      const hasImages =
        (referenceImages && referenceImages.length > 0) ||
        (itemImages && itemImages.length > 0);

      if (hasImages) {
        const formData = createJobFormData(
          jobData,
          referenceImages,
          itemImages
        );
        const response = await api.post(
          `${ADMIN_JOBS_URL}/alteration`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        return response.data;
      } else {
        const response = await api.post(
          `${ADMIN_JOBS_URL}/alteration`,
          jobData
        );
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const createInternalJob = createAsyncThunk(
  "karigar/job/createInternal",
  async ({ jobData, referenceImages }, { rejectWithValue }) => {
    try {
      const hasImages = referenceImages && referenceImages.length > 0;

      if (hasImages) {
        const formData = createJobFormData(jobData, referenceImages, []);
        const response = await api.post(
          `${ADMIN_JOBS_URL}/internal`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        return response.data;
      } else {
        const response = await api.post(`${ADMIN_JOBS_URL}/internal`, jobData);
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const receiveItemForWork = createAsyncThunk(
  "karigar/job/receiveItem",
  async ({ itemData, itemImages }, { rejectWithValue }) => {
    try {
      const hasImages = itemImages && itemImages.length > 0;

      if (hasImages) {
        const formData = new FormData();
        Object.keys(itemData).forEach((key) => {
          const value = itemData[key];
          if (value !== undefined && value !== null) {
            if (typeof value === "object" && !(value instanceof File)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        itemImages.forEach((file) => {
          formData.append("itemImages", file);
        });

        const response = await api.post(
          `${ADMIN_JOBS_URL}/receive-item`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data;
      } else {
        const response = await api.post(
          `${ADMIN_JOBS_URL}/receive-item`,
          itemData
        );
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// JOB EDITING & STATUS
// ============================================================================

export const updateJob = createAsyncThunk(
  "karigar/job/update",
  async ({ jobId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${ADMIN_JOBS_URL}/${jobId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const updateJobStatus = createAsyncThunk(
  "karigar/job/updateStatus",
  async (
    { jobId, newStatus, notes, forceTransition = false },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`${ADMIN_JOBS_URL}/${jobId}/status`, {
        newStatus,
        notes,
        forceTransition,
      });
      return { jobId, newStatus, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const assignJob = createAsyncThunk(
  "karigar/job/assign",
  async ({ jobId, karigarId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${ADMIN_JOBS_URL}/${jobId}/assign`, {
        karigarId,
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const reassignJob = createAsyncThunk(
  "karigar/job/reassign",
  async ({ jobId, newKarigarId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${ADMIN_JOBS_URL}/${jobId}/assign`, {
        karigarId: newKarigarId,
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const cancelJob = createAsyncThunk(
  "karigar/job/cancel",
  async ({ jobId, reason, materialReturned = false }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${ADMIN_JOBS_URL}/${jobId}/cancel`, {
        reason,
        materialReturned,
      });
      return { jobId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const cancelJobs = createAsyncThunk(
  "karigar/job/cancelMultiple",
  async ({ jobIds, reason }, { rejectWithValue }) => {
    try {
      const results = await Promise.all(
        jobIds.map((jobId) =>
          api.put(`${ADMIN_JOBS_URL}/${jobId}/cancel`, { reason })
        )
      );
      return { success: true, count: results.length };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// IMAGE MANAGEMENT
// ============================================================================

export const fetchJobImages = createAsyncThunk(
  "karigar/job/fetchImages",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_JOBS_URL}/${jobId}/images`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const uploadReferenceImages = createAsyncThunk(
  "karigar/job/uploadReferenceImages",
  async ({ jobId, images }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      images.forEach((file) => {
        formData.append("referenceImages", file);
      });

      const response = await api.post(
        `${ADMIN_JOBS_URL}/${jobId}/images/reference`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const deleteReferenceImage = createAsyncThunk(
  "karigar/job/deleteReferenceImage",
  async ({ jobId, imageUrl }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${ADMIN_JOBS_URL}/${jobId}/images/reference`,
        { data: { imageUrl } }
      );
      return { jobId, imageUrl, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const uploadCompletedImages = createAsyncThunk(
  "karigar/job/uploadCompletedImages",
  async ({ jobId, images }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      images.forEach((file) => {
        formData.append("completedImages", file);
      });

      const response = await api.post(
        `${ADMIN_JOBS_URL}/${jobId}/images/completed`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const deleteCompletedImage = createAsyncThunk(
  "karigar/job/deleteCompletedImage",
  async ({ jobId, imageUrl }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${ADMIN_JOBS_URL}/${jobId}/images/completed`,
        { data: { imageUrl } }
      );
      return { jobId, imageUrl, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const uploadItemImages = createAsyncThunk(
  "karigar/job/uploadItemImages",
  async ({ jobId, images }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      images.forEach((file) => {
        formData.append("itemImages", file);
      });

      const response = await api.post(
        `${ADMIN_JOBS_URL}/${jobId}/images/item`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const uploadQCImages = createAsyncThunk(
  "karigar/job/uploadQCImages",
  async ({ jobId, images }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      images.forEach((file) => {
        formData.append("images", file);
      });

      const response = await api.post(
        `${ADMIN_JOBS_URL}/${jobId}/images/qc`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const bulkDeleteImages = createAsyncThunk(
  "karigar/job/bulkDeleteImages",
  async ({ jobId, imageUrls, imageType }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${ADMIN_JOBS_URL}/${jobId}/images/bulk`,
        { data: { imageUrls, imageType } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const uploadDesignFile = createAsyncThunk(
  "karigar/job/uploadDesignFile",
  async ({ jobId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await api.post(
        `${ADMIN_JOBS_URL}/${jobId}/design-file`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// MATERIAL MANAGEMENT - COMPLETE CRUD (with image upload support)
// ============================================================================

/**
 * Fetch materials for a job
 */
export const fetchJobMaterials = createAsyncThunk(
  "karigar/job/fetchMaterials",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_JOBS_URL}/${jobId}/materials`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Issue material to karigar
 */
export const issueMaterial = createAsyncThunk(
  "karigar/job/issueMaterial",
  async ({ jobId, materialData, images }, { rejectWithValue }) => {
    try {
      const hasImages = images && images.length > 0;

      if (hasImages) {
        const formData = new FormData();
        Object.keys(materialData).forEach((key) => {
          const value = materialData[key];
          if (value !== undefined && value !== null) {
            if (typeof value === "object" && !(value instanceof File)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        images.forEach((file) => {
          formData.append("images", file);
        });

        const response = await api.post(
          `${ADMIN_JOBS_URL}/${jobId}/materials/issue`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data;
      } else {
        const response = await api.post(
          `${ADMIN_JOBS_URL}/${jobId}/materials/issue`,
          materialData
        );
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Update issued material
 */
export const updateIssuedMaterial = createAsyncThunk(
  "karigar/job/updateIssuedMaterial",
  async (
    { jobId, materialId, materialData, forceUpdate },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(
        `${ADMIN_JOBS_URL}/${jobId}/materials/${materialId}`,
        { ...materialData, forceUpdate }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Delete issued material
 */
export const deleteIssuedMaterial = createAsyncThunk(
  "karigar/job/deleteIssuedMaterial",
  async ({ jobId, materialId, reason, forceDelete }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${ADMIN_JOBS_URL}/${jobId}/materials/${materialId}`,
        { data: { reason, forceDelete } }
      );
      return { jobId, materialId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Confirm karigar received material
 */
export const confirmMaterialReceived = createAsyncThunk(
  "karigar/job/confirmMaterialReceived",
  async ({ jobId, materialId, notes }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${ADMIN_JOBS_URL}/${jobId}/materials/${materialId}/received`,
        { notes }
      );
      return { jobId, materialId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Undo material received confirmation
 */
export const undoMaterialReceived = createAsyncThunk(
  "karigar/job/undoMaterialReceived",
  async ({ jobId, materialId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${ADMIN_JOBS_URL}/${jobId}/materials/${materialId}/undo-received`,
        { reason }
      );
      return { jobId, materialId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Record material return from karigar
 */
export const recordMaterialReturn = createAsyncThunk(
  "karigar/job/recordMaterialReturn",
  async ({ jobId, returnData, images }, { rejectWithValue }) => {
    try {
      const hasImages = images && images.length > 0;

      if (hasImages) {
        const formData = new FormData();
        Object.keys(returnData).forEach((key) => {
          const value = returnData[key];
          if (value !== undefined && value !== null) {
            if (typeof value === "object" && !(value instanceof File)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        images.forEach((file) => {
          formData.append("images", file);
        });

        const response = await api.post(
          `${ADMIN_JOBS_URL}/${jobId}/materials/return`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data;
      } else {
        const response = await api.post(
          `${ADMIN_JOBS_URL}/${jobId}/materials/return`,
          returnData
        );
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Update/Adjust material return
 */
export const updateMaterialReturn = createAsyncThunk(
  "karigar/job/updateMaterialReturn",
  async (
    { jobId, returnData, adjustmentReason, images },
    { rejectWithValue }
  ) => {
    try {
      const hasImages = images && images.length > 0;

      if (hasImages) {
        const formData = new FormData();
        Object.keys(returnData).forEach((key) => {
          const value = returnData[key];
          if (value !== undefined && value !== null) {
            if (typeof value === "object" && !(value instanceof File)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        if (adjustmentReason)
          formData.append("adjustmentReason", adjustmentReason);
        images.forEach((file) => formData.append("images", file));

        const response = await api.put(
          `${ADMIN_JOBS_URL}/${jobId}/materials/return`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data;
      } else {
        const response = await api.put(
          `${ADMIN_JOBS_URL}/${jobId}/materials/return`,
          { ...returnData, adjustmentReason }
        );
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Delete material return record (reset)
 */
export const deleteMaterialReturn = createAsyncThunk(
  "karigar/job/deleteMaterialReturn",
  async ({ jobId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${ADMIN_JOBS_URL}/${jobId}/materials/return`,
        { data: { reason } }
      );
      return { jobId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Legacy alias
export const updateJobMaterials = issueMaterial;

// ============================================================================
// QUALITY CHECK WORKFLOW (with image upload support)
// ============================================================================

export const submitForQualityCheck = createAsyncThunk(
  "karigar/job/submitQC",
  async ({ jobId, notes, images, finishedWeight }, { rejectWithValue }) => {
    try {
      const hasImages = images && images.length > 0;

      if (hasImages) {
        const formData = new FormData();
        if (notes) formData.append("notes", notes);
        if (finishedWeight) formData.append("finishedWeight", finishedWeight);
        images.forEach((file) => {
          formData.append("images", file);
        });

        const response = await api.put(
          `${ADMIN_JOBS_URL}/${jobId}/submit-qc`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data;
      } else {
        const response = await api.put(`${ADMIN_JOBS_URL}/${jobId}/submit-qc`, {
          notes,
          finishedWeight,
        });
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const performQualityCheck = createAsyncThunk(
  "karigar/job/qualityCheck",
  async (
    {
      jobId,
      passed,
      rating,
      score,
      notes,
      finalWeight,
      finalPurity,
      hallmarkNumber,
      revisionReason,
      images,
    },
    { rejectWithValue }
  ) => {
    try {
      const hasImages = images && images.length > 0;

      if (hasImages) {
        const formData = new FormData();
        formData.append("passed", passed);
        if (rating) formData.append("rating", rating);
        if (score !== undefined) formData.append("score", score);
        if (notes) formData.append("notes", notes);
        if (finalWeight) formData.append("finalWeight", finalWeight);
        if (finalPurity) formData.append("finalPurity", finalPurity);
        if (hallmarkNumber) formData.append("hallmarkNumber", hallmarkNumber);
        if (revisionReason) formData.append("revisionReason", revisionReason);
        images.forEach((file) => {
          formData.append("images", file);
        });

        const response = await api.put(
          `${ADMIN_JOBS_URL}/${jobId}/quality-check`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data;
      } else {
        const response = await api.put(
          `${ADMIN_JOBS_URL}/${jobId}/quality-check`,
          {
            passed,
            rating,
            score,
            notes,
            finalWeight,
            finalPurity,
            hallmarkNumber,
            revisionReason,
          }
        );
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const completeRevision = createAsyncThunk(
  "karigar/job/completeRevision",
  async ({ jobId, notes, images }, { rejectWithValue }) => {
    try {
      const hasImages = images && images.length > 0;

      if (hasImages) {
        const formData = new FormData();
        if (notes) formData.append("notes", notes);
        images.forEach((file) => {
          formData.append("images", file);
        });

        const response = await api.put(
          `${ADMIN_JOBS_URL}/${jobId}/revision-complete`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data;
      } else {
        const response = await api.put(
          `${ADMIN_JOBS_URL}/${jobId}/revision-complete`,
          { notes }
        );
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const markJobDelivered = createAsyncThunk(
  "karigar/job/deliver",
  async ({ jobId, notes, deliveredTo }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${ADMIN_JOBS_URL}/${jobId}/deliver`, {
        notes,
        deliveredTo,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Legacy aliases
export const approveJob = (params) =>
  performQualityCheck({ ...params, passed: true });
export const rejectJob = ({ jobId, reason }) =>
  performQualityCheck({ jobId, passed: false, revisionReason: reason });
export const requestRework = ({ jobId, reason, details }) =>
  performQualityCheck({
    jobId,
    passed: false,
    revisionReason: reason,
    notes: details,
  });
export const completeJob = submitForQualityCheck;

// ============================================================================
// JOB PAYMENT MANAGEMENT
// ============================================================================

/**
 * Fetch payment summary for a job
 * GET /api/admin-karigar/jobs/:jobId/payments
 *
 * Returns: payments, pricing breakdown, karigar payable amount
 */
export const fetchJobPayments = createAsyncThunk(
  "karigar/job/fetchPayments",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_JOBS_URL}/${jobId}/payments`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Update job pricing settings
 * PUT /api/admin-karigar/jobs/:jobId/pricing
 */
export const updateJobPricing = createAsyncThunk(
  "karigar/job/updatePricing",
  async ({ jobId, pricingData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${ADMIN_JOBS_URL}/${jobId}/pricing`,
        pricingData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Record payment for job (creates KarigarPayment record)
 * POST /api/admin-karigar/jobs/:jobId/payments
 *
 * Creates a payment record. If processImmediately=true, payment is completed
 * immediately and job's paidAmount is updated. Otherwise, creates a pending
 * payment that needs to be processed separately.
 */
export const recordJobPayment = createAsyncThunk(
  "karigar/job/recordPayment",
  async ({ jobId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${ADMIN_JOBS_URL}/${jobId}/payments`,
        paymentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Add deduction to job
 * POST /api/admin-karigar/jobs/:jobId/deductions
 */
export const addJobDeduction = createAsyncThunk(
  "karigar/job/addDeduction",
  async ({ jobId, deductionData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${ADMIN_JOBS_URL}/${jobId}/deductions`,
        deductionData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Remove deduction from job (NEW)
 * DELETE /api/admin-karigar/jobs/:jobId/deductions/:deductionIndex
 */
export const removeJobDeduction = createAsyncThunk(
  "karigar/job/removeDeduction",
  async ({ jobId, deductionIndex }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${ADMIN_JOBS_URL}/${jobId}/deductions/${deductionIndex}`
      );
      return { jobId, deductionIndex, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch pricing calculation preview for a job
 * GET /api/admin-karigar/jobs/:jobId/pricing-preview
 *
 * Returns preview of labor calculations across all rate types
 */
export const fetchPricingPreview = createAsyncThunk(
  "karigar/job/fetchPricingPreview",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${ADMIN_JOBS_URL}/${jobId}/pricing-preview`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// PROGRESS
// ============================================================================

export const addJobProgress = createAsyncThunk(
  "karigar/job/addProgress",
  async ({ jobId, progressPercentage, notes, images }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${ADMIN_JOBS_URL}/${jobId}/progress`, {
        progressPercentage,
        notes,
        images,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// FETCH BY RELATIONS
// ============================================================================

export const fetchJobsByKarigar = createAsyncThunk(
  "karigar/job/fetchByKarigar",
  async ({ karigarId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(ADMIN_JOBS_URL, {
        params: { ...params, karigarId },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const fetchJobsByOrder = createAsyncThunk(
  "karigar/job/fetchByOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(ADMIN_JOBS_URL, { params: { orderId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const fetchJobTimeline = createAsyncThunk(
  "karigar/job/fetchTimeline",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_JOBS_URL}/${jobId}`);
      return response.data?.data?.job?.timeline || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// ORDER-KARIGAR INTEGRATION
// ============================================================================

export const fetchAvailableKarigarsForJob = createAsyncThunk(
  "karigar/job/fetchAvailableKarigars",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${ORDER_KARIGAR_URL}/available-karigars`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const assignKarigarToOrderItem = createAsyncThunk(
  "karigar/job/assignToOrderItem",
  async (
    { orderId, orderItemId, karigarId, expectedCompletionDate, notes },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`${ORDER_KARIGAR_URL}/assign`, {
        orderId,
        orderItemId,
        karigarId,
        expectedCompletionDate,
        notes,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const reassignKarigarForOrderItem = createAsyncThunk(
  "karigar/job/reassignForOrderItem",
  async (
    { orderId, orderItemId, jobId, newKarigarId, reason },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`${ORDER_KARIGAR_URL}/reassign`, {
        orderId,
        orderItemId,
        jobId,
        newKarigarId,
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const bulkAssignKarigars = createAsyncThunk(
  "karigar/job/bulkAssign",
  async (assignments, { rejectWithValue }) => {
    try {
      const response = await api.post(`${ORDER_KARIGAR_URL}/bulk-assign`, {
        assignments,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const getOrderKarigarStatus = createAsyncThunk(
  "karigar/job/getOrderStatus",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${ORDER_KARIGAR_URL}/order/${orderId}/status`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const getKarigarQueue = createAsyncThunk(
  "karigar/job/getKarigarQueue",
  async (karigarId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${ORDER_KARIGAR_URL}/karigar/${karigarId}/queue`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// ORDER-KARIGAR VIA ORDER SERVICE
// ============================================================================

export const getOrderKarigarWorkStatus = createAsyncThunk(
  "karigar/job/getOrderKarigarWorkStatus",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/orders/admin/order/${orderId}/karigar`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const assignKarigarViaOrder = createAsyncThunk(
  "karigar/job/assignViaOrder",
  async (
    { orderId, itemIndex, karigarId, workDetails },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`${ORDER_KARIGAR_URL}/assign`, {
        orderId,
        itemIndex,
        karigarId,
        workDetails,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

export const checkOrderCanFulfill = createAsyncThunk(
  "karigar/job/checkCanFulfill",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/orders/admin/order/${orderId}/karigar/can-fulfill`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);
